import axios from "axios";
import { API_BASE_URL } from "../../config";
import { authHeaders } from "../utils/session";

/**
 * Smart Search plans — the packages a customer buys search quota under.
 *
 * Separate from the subscription plans in `manage-plan-new`: those sell the
 * trademark product, these sell searches and brand watches, and the two have
 * their own codes, tiers and pricing.
 */

const url = (path = "") => `${API_BASE_URL}/smart-search/plans${path}`;

/**
 * One reading of a failure, for every call here.
 *
 * The API puts its explanation in the body on a non-2xx, so axios's own
 * "Request failed with status code 422" must never reach a toast — it tells the
 * reader nothing they can act on. `status` and `errors` ride along so a caller
 * can tell a validation failure from a conflict without re-parsing the error.
 */
const apiError = (err, fallback) => {
  const res = err?.response;
  const body = res?.data || {};
  const e = new Error(body.msg || body.message || body.error || fallback);
  e.status = body.status || res?.status;
  e.errors = body.errors || null;
  return e;
};

/**
 * Some endpoints answer 200 with a failure in the body.
 *
 * A validation error in particular comes back as HTTP 200 with
 * `{ status: 422, errors: {...} }`, so a call that only checks the catch block
 * silently treats a rejected save as a success.
 */
const unwrap = (res, fallback) => {
  const body = res?.data || {};
  const status = Number(body.status);
  if (status && status >= 400) {
    const e = new Error(body.msg || body.message || fallback);
    e.status = status;
    e.errors = body.errors || null;
    throw e;
  }
  return body;
};

/**
 * The choices the plan form offers.
 *
 * Fetched rather than listed in code: price modes and tiers live in the
 * database, and a hardcoded list goes stale the first time someone adds one
 * there. Cached for the life of the page — this is reference data, and
 * refetching it every time the drawer opens buys nothing.
 *
 * @returns {Promise<{price_modes: Array, tiers: Array}>}
 */
let metaPromise = null;
export const fetchSmartSearchMeta = () => {
  if (!metaPromise) {
    metaPromise = axios
      .get(`${API_BASE_URL}/smart-search/plans/meta`, { headers: authHeaders() })
      .then((res) => {
        const body = res?.data || {};
        return body.data && typeof body.data === "object" ? body.data : body;
      })
      /* A form that cannot offer the full list is still a usable form — it
         falls back to what it knows. Cleared so a later open can try again. */
      .catch(() => { metaPromise = null; return {}; });
  }
  return metaPromise;
};

/** @returns {{data: Array}} every plan, active and not. */
export const fetchSmartSearchPlans = async () => {
  try {
    const res = await axios.get(url(), { headers: authHeaders() });
    const body = unwrap(res, "Plans could not be loaded");
    return Array.isArray(body.data) ? body.data : (Array.isArray(body) ? body : []);
  } catch (err) {
    throw apiError(err, "Plans could not be loaded");
  }
};

export const createSmartSearchPlan = async (payload) => {
  try {
    const res = await axios.post(url(), payload, { headers: authHeaders() });
    return unwrap(res, "The plan could not be created");
  } catch (err) {
    throw apiError(err, "The plan could not be created");
  }
};

export const updateSmartSearchPlan = async (id, payload) => {
  try {
    const res = await axios.put(url(`/${id}`), payload, { headers: authHeaders() });
    return unwrap(res, "The plan could not be saved");
  } catch (err) {
    throw apiError(err, "The plan could not be saved");
  }
};

/**
 * Set a plan active or inactive.
 *
 * The intended value is sent rather than left implicit. A bare "flip whatever
 * it is now" is only correct if the browser's idea of the current state is
 * right — two people on the screen at once, or a stale list, and a flip lands
 * the wrong way round. Stating the target makes the call idempotent; a backend
 * that ignores the body and flips anyway still works, and either way the
 * response's own `is_active` is what the UI believes.
 *
 * POST rather than PATCH: the backend takes either, and PATCH is the one verb
 * a proxy or a CORS preflight in front of it may not be configured to let
 * through.
 *
 * @param {number|string} id
 * @param {0|1} isActive the state the plan should end up in
 */
export const toggleSmartSearchPlan = async (id, isActive) => {
  try {
    const res = await axios.post(
      url(`/${id}/toggle`),
      isActive === undefined ? {} : { is_active: Number(isActive) },
      { headers: authHeaders() },
    );
    return unwrap(res, "The status could not be changed");
  } catch (err) {
    throw apiError(err, "The status could not be changed");
  }
};

/**
 * Remove a plan.
 *
 * A 409 here means customers are still subscribed to it; the caller turns that
 * into an offer to deactivate instead, which is the thing the user actually
 * wanted in that case.
 */
export const deleteSmartSearchPlan = async (id) => {
  try {
    const res = await axios.delete(url(`/${id}`), { headers: authHeaders() });
    return unwrap(res, "The plan could not be deleted");
  } catch (err) {
    throw apiError(err, "The plan could not be deleted");
  }
};

// ── Customers ────────────────────────────────────────────────────────────────
const custUrl = (path = "") => `${API_BASE_URL}/smart-search/customers${path}`;

/**
 * Only what has a value.
 *
 * An empty search term sent as `search=` is a filter for the empty string on
 * some backends and ignored on others; dropping it means "no filter" reads as
 * no parameter, which every backend agrees on.
 */
const clean = (params) => {
  const out = {};
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v !== "" && v !== null && v !== undefined && v !== "all") out[k] = v;
  });
  return out;
};

/**
 * A paged response, however this endpoint spells it.
 *
 * `total` is the one field the pager cannot do without, and it has been seen as
 * `total`, `count` and nested under `pagination` — read once here rather than
 * guessed at each call site.
 */
const paged = (body) => {
  const rows = Array.isArray(body?.data) ? body.data : [];
  const p = body?.pagination || {};
  const perPage = Number(body?.per_page ?? p.per_page ?? p.limit ?? rows.length) || rows.length;
  const total = Number(body?.total ?? body?.count ?? p.total ?? rows.length);
  return {
    rows,
    total,
    currentPage: Number(body?.current_page ?? p.current_page ?? p.page ?? 1),
    perPage,
    /* Derived only when the server does not say. An endpoint that reports
       neither leaves `lastPage` at 1, which stops the scroller rather than
       looping on a page that will never arrive. */
    lastPage: Number(
      body?.last_page ?? p.last_page ?? p.total_pages
      ?? (perPage > 0 ? Math.max(1, Math.ceil(total / perPage)) : 1),
    ),
  };
};

/**
 * One subscriber, found by the customer rather than by their subscription.
 *
 * The profile knows a user id; every other Smart Search endpoint keys off the
 * subscription id. This is the bridge, and it returns the same body as
 * `/customers/:id`, so the caller reads the subscription id out of it and uses
 * that for watches and usage.
 *
 * A 404 is the honest answer for "this customer has no Smart Search plan", so
 * it is not an error the caller should show — `status` rides on the thrown
 * error for exactly that check.
 */
export const fetchSmartSearchCustomerByUser = async (userId) => {
  try {
    const res = await axios.get(custUrl(`/by-user/${userId}`), { headers: authHeaders() });
    const body = unwrap(res, "This customer could not be loaded");
    return body.data && typeof body.data === "object" ? body.data : body;
  } catch (err) {
    throw apiError(err, "This customer could not be loaded");
  }
};

export const fetchCustomerWatches = async (id, params) => {
  try {
    const res = await axios.get(custUrl(`/${id}/watches`), { params: clean(params), headers: authHeaders() });
    return paged(unwrap(res, "Watched brands could not be loaded"));
  } catch (err) {
    throw apiError(err, "Watched brands could not be loaded");
  }
};

export const fetchCustomerUsage = async (id, params) => {
  try {
    const res = await axios.get(custUrl(`/${id}/usage`), { params: clean(params), headers: authHeaders() });
    return paged(unwrap(res, "The search log could not be loaded"));
  } catch (err) {
    throw apiError(err, "The search log could not be loaded");
  }
};
